/**
 * Veluna Capture Guard - the single arbitration authority for intrusive
 * email capture.
 *
 * POLICY ONLY. It renders nothing, owns no UI, and stores no subscriber data.
 * Each capture engine keeps its own triggers, storage, cooldowns and submit
 * handler; this only answers one question: "may anything interrupt right now?"
 *
 * Replaces the reference to veluna-luna-decision-engine.js, which 404'd on
 * every page since the 2026-08-16 asset cleanup. That file defined
 * window.LunaDecisionEngine (three pure helper functions) and NOTHING in the
 * theme ever called it - restoring it would have restored a no-op. Retired.
 *
 * Rules implemented (deliberately only these):
 *   1. One intrusive overlay at a time.
 *   2. Purchase configuration on screen suppresses capture.
 *   3. A purchase action just taken suppresses capture.
 *   4. An email already captured suppresses further prompts.
 * Existing dismissal/frequency memory is untouched and still authoritative -
 * this guard can only ever say "no", never "yes".
 */
(function (w, d) {
  'use strict';
  if (w.VelunaCaptureGuard) return;

  /* Purchase-configuration blocks on the PDP and the quiz result page. */
  var COMMERCE_SEL = [
    '[data-veluna-purchase-type]',
    '[data-mr-purchase-type]',
    '.recovery-cream-hero__velunasub',
    '[data-velunasub-schedule]',
    '[data-velunasub-onetime]',
    '[data-mr-schedule]',
    /* 2026-09-23: the homepage set cards and the PDP add-to-cart are purchase
       controls too. A capture may not open while any of them is on screen. */
    '.vrs__buy',
    '.vrs__card',
    '.vrs__btn',
    '#recovery-cream-add-to-cart',
    '[data-veluna-atc]'
  ].join(',');

  /* Controls that mean "this visitor is transacting". */
  var PURCHASE_SEL = '[data-veluna-atc],#recovery-cream-add-to-cart,#mr-cta,' +
                     '#mr-v2-sticky-btn,#mr-cta-dup,#mr-v2-cta2-btn,' +
                     '[name="checkout"],a[href*="/checkout"]';

  /* Overlays owned by the capture engines, used for the one-at-a-time check.
     The unified-popup and the article/blog popup were previously invisible to
     this list, which is why the homepage could render two full-viewport capture
     overlays at once at every tested width. */
  var OVERLAY_SEL = '.vpm__overlay.is-open,.vqp__overlay.is-open,.mr-sheet.is-open,' +
                    '.luna-popup,[data-luna-popup],' +
                    '.unified-popup.unified-popup--visible,' +
                    '.veluna-fs-overlay.is-open,[data-veluna-article-popup].is-open,' +
                    '.veluna-exit-intent-modal--visible,.veluna-fs-popup.is-open,' +
                    '.vqx__overlay.is-open';

  /* Arbitration priority when two engines become eligible in the same tick.
     Higher wins. Rule: a PAGE-SPECIFIC capture outranks the generic site-wide
     one, because it is the offer the visitor's current context earned.
     Equal priority falls back to first-to-acquire, which the owner lock already
     enforces. */
  var PRIORITY = {
    'pdp-mystery': 30,
    'quiz-results-exit': 30,
    'quiz-exit': 30,
    'article-capture': 20,
    'pdf-capture': 10,
    'unified-popup': 5
  };
  function rank(id) { return PRIORITY[id] != null ? PRIORITY[id] : 10; }

  /* PRECEDENCE, not preemption.
     A page-specific engine announces itself at load. The generic site-wide
     capture then stands down on that page instead of racing it to the guard.
     This is what makes the outcome deterministic: whichever engine happens to
     trigger first no longer decides which lane owns the page. */
  var pageSpecific = [];
  /* Engines may load before OR after this file, so the queue is read live on
     every query rather than drained once at init. Script order cannot change
     the outcome. */
  function allPageSpecific() {
    var out = pageSpecific.slice();
    try {
      (w.__vlnPageSpecific || []).forEach(function (id) {
        if (id && out.indexOf(id) === -1) out.push(id);
      });
    } catch (_) {}
    return out;
  }

  var PURCHASE_WINDOW_MS = 8000;

  /* 2026-09-23. Session memory the guard owns:
     - an add-to-cart this session ends all capture prompts for the session;
     - dismissing a capture ends THAT ENGINE's prompts for the session
       (each engine also writes its own 7-day key).
     2026-10-01 (owner): the dismissal is remembered per engine, key
     veluna_capture_dismissed_session:<id>. Closing the guide popup no longer
     silences the PDP offer and vice versa. The old single key
     'veluna_capture_dismissed_session' is no longer written or read. */
  var ATC_SESSION_KEY = 'veluna_atc_this_session';
  var DISMISS_SESSION_KEY = 'veluna_capture_dismissed_session';
  function dismissKey(id) { return DISMISS_SESSION_KEY + ':' + (id || 'unknown'); }
  /* 2026-10-01: a capture older than this no longer silences the PDP offer. */
  var CAPTURE_RECENT_MS = 30 * 24 * 60 * 60 * 1000;
  var REOFFER_AFTER_MS = { 'pdp-mystery': CAPTURE_RECENT_MS };
  function ssGet(k) { try { return w.sessionStorage.getItem(k); } catch (_) { return null; } }
  function ssSet(k, v) { try { w.sessionStorage.setItem(k, v); } catch (_) {} }
  function markAddToCart() { ssSet(ATC_SESSION_KEY, String(Date.now())); }

  var commerceOnScreen = false;
  var lastPurchaseIntent = 0;
  var currentOwner = null;

  function onScreen(el) {
    if (!el) return false;
    var cs = w.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (w.innerHeight || 0) &&
           r.right > 0 && r.left < (w.innerWidth || 0) && r.width > 1 && r.height > 1;
  }

  /* Rule 2 - IntersectionObserver, not a timer: the page tells us where the
     visitor is. Re-scanned on DOM changes because the quiz result page builds
     its commerce block client-side after the recommendation resolves. */
  var observer = null;
  var watched = [];
  function watchCommerce() {
    if (!w.IntersectionObserver) return;
    var nodes = [].slice.call(d.querySelectorAll(COMMERCE_SEL));
    if (!nodes.length) return;
    if (!observer) {
      observer = new w.IntersectionObserver(function () {
        commerceOnScreen = watched.some(onScreen);
      }, { threshold: 0.15 });
    }
    nodes.forEach(function (n) {
      if (watched.indexOf(n) === -1) { watched.push(n); observer.observe(n); }
    });
    commerceOnScreen = watched.some(onScreen);
  }

  /* Rule 3 - capture purchase intent in the capture phase so it registers
     even when a handler stops propagation. */
  d.addEventListener('click', function (e) {
    try {
      if (e.target && e.target.closest && e.target.closest(PURCHASE_SEL)) {
        lastPurchaseIntent = Date.now();
        if (e.target.closest(ATC_SEL)) markAddToCart();
      }
    } catch (_) {}
  }, true);

  /* Add-to-cart, whatever the widget: a form posting to /cart/add, or any
     fetch/XHR to /cart/add (seen through Resource Timing, so nothing is
     wrapped or patched). */
  var ATC_SEL = '[data-veluna-atc],#recovery-cream-add-to-cart';
  d.addEventListener('submit', function (e) {
    try {
      var a = (e.target && e.target.getAttribute && e.target.getAttribute('action')) || '';
      if (a.indexOf('/cart/add') !== -1) markAddToCart();
    } catch (_) {}
  }, true);
  try {
    if (w.PerformanceObserver) {
      new w.PerformanceObserver(function (list) {
        list.getEntries().forEach(function (en) {
          if (en && typeof en.name === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(en.name.split('#')[0].replace(/^https?:\/\/[^/]+/, ''))) markAddToCart();
        });
      }).observe({ type: 'resource', buffered: true });
    }
  } catch (_) {}

  /* Rule 4 - read the engines' OWN existing keys. No new storage is created,
     so dismissal/frequency memory keeps working exactly as before. */
  function captureAgeMs() {
    /* veluna_pdf_captured = '<email>|<ms>' (every capture writes it). null = unknown age. */
    try {
      var ms = parseInt((localStorage.getItem('veluna_pdf_captured') || '').split('|')[1] || '0', 10);
      if (ms > 0) return Date.now() - ms;
    } catch (_) {}
    return null;
  }
  function alreadyCaptured(id) {
    var captured = false;
    try {
      if (localStorage.getItem('veluna_pdf_captured')) captured = true;
      else if (localStorage.getItem('veluna_email_captured') === 'true') captured = true;
      else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((localStorage.getItem('veluna_quiz_email') || '').trim())) captured = true;
    } catch (_) {}
    try {
      if (!captured && typeof w.hasEmailCaptured === 'function' && w.hasEmailCaptured()) captured = true;
    } catch (_) {}
    if (!captured) return false;
    /* 2026-10-01: the PDP offer may ask again once the capture is older than 30 days.
       An unknown age (legacy flags without a timestamp) counts as recent. */
    var reoffer = id && REOFFER_AFTER_MS[id];
    if (reoffer) { var age = captureAgeMs(); if (age !== null && age > reoffer) return false; }
    return true;
  }

  /* Rule 2, synchronous (2026-09-23): the IntersectionObserver registers
     after the engines' first checks, so it could report "not visible" while
     the buy box was on screen. mayShow() now measures right now. */
  function commerceVisibleNow() {
    var nodes = d.querySelectorAll(COMMERCE_SEL);
    for (var i = 0; i < nodes.length; i++) if (onScreen(nodes[i])) return true;
    return false;
  }

  /* Rule 1 - is any intrusive overlay already on screen? */
  function overlayOpen() {
    var nodes = d.querySelectorAll(OVERLAY_SEL);
    for (var i = 0; i < nodes.length; i++) if (onScreen(nodes[i])) return true;
    return false;
  }

  /* Priority of the highest-ranked overlay currently on screen. */
  function highestOpenRank() {
    var nodes = d.querySelectorAll(OVERLAY_SEL), best = 0;
    for (var i = 0; i < nodes.length; i++) {
      if (!onScreen(nodes[i])) continue;
      var owner = nodes[i].getAttribute('data-capture-id') || '';
      var r = owner ? rank(owner) : 10;
      if (r > best) best = r;
    }
    return best;
  }

  function suppressReason(id) {
    if (alreadyCaptured(id)) return 'already_captured';
    /* Self-healing: an engine that closed its overlay without telling us must
       not be able to deadlock every other surface for the rest of the session. */
    if (currentOwner && !overlayOpen()) currentOwner = null;
    if (currentOwner && currentOwner !== id) return 'overlay_open:' + currentOwner;
    /* A lower-priority engine may not open on top of a higher-priority one that
       is already on screen, even if that engine never claimed ownership. */
    if (!currentOwner && overlayOpen() && rank(id) < highestOpenRank()) {
      return 'lower_priority_than_open_overlay';
    }
    if (overlayOpen()) return 'overlay_on_screen';
    if (ssGet(ATC_SESSION_KEY)) return 'added_to_cart_this_session';
    if (ssGet(dismissKey(id))) return 'dismissed_this_session';
    commerceOnScreen = commerceVisibleNow();
    if (commerceOnScreen) return 'commerce_configuration_visible';
    if (Date.now() - lastPurchaseIntent < PURCHASE_WINDOW_MS) return 'purchase_in_progress';
    return null;
  }

  /* One shared modal helper for every capture overlay (.vqp, .vpm, .vqx).
     holdFocus(root, {focus, initial, onEscape}) -> release()
       - focuses the first control (or `initial`, a selector) unless focus:false
       - keeps Tab / Shift+Tab inside root
       - calls onEscape on Esc when given (engines that already close on Esc
         do not pass it)
       - while held, hides the price bar and puts the sticky header, trust
         strip and article quiz bar out of reach behind the overlay
       - release() restores focus to what had it before. */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
                  'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function injectChromeStyles() {
    if (d.getElementById('vln-capture-chrome')) return;
    var st = d.createElement('style');
    st.id = 'vln-capture-chrome';
    st.textContent =
      'html.vln-capture-open .vpb,html.vln-capture-open .vlcb-sticky{display:none!important;}' +
      'html.vln-capture-open #shopify-section-header,html.vln-capture-open .shopify-section-header,' +
      'html.vln-capture-open .veluna-header,html.vln-capture-open .veluna-cart-trust{pointer-events:none!important;}' +
      '.vqp__overlay,.vpm__overlay,.vqx__overlay{z-index:2147483000!important;}';
    (d.head || d.documentElement).appendChild(st);
  }
  var held = 0;
  function holdFocus(root, opts) {
    opts = opts || {};
    injectChromeStyles();
    var prev = d.activeElement;
    function visible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
    function items() { return [].slice.call(root.querySelectorAll(FOCUSABLE)).filter(visible); }
    function onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') { if (opts.onEscape) { e.preventDefault(); opts.onEscape(); } return; }
      if (e.key !== 'Tab') return;
      var f = items();
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0], last = f[f.length - 1], a = d.activeElement;
      if (e.shiftKey) {
        if (a === first || !root.contains(a)) { e.preventDefault(); last.focus(); }
      } else if (a === last || !root.contains(a)) { e.preventDefault(); first.focus(); }
    }
    d.addEventListener('keydown', onKey, true);
    held++;
    d.documentElement.classList.add('vln-capture-open');
    if (opts.focus !== false) {
      setTimeout(function () {
        var t = null;
        if (opts.initial) t = [].slice.call(root.querySelectorAll(opts.initial)).filter(visible)[0] || null;
        if (!t) t = items().filter(function (el) { return !el.hasAttribute('data-close') && !el.hasAttribute('data-vqx-close'); })[0] || items()[0];
        if (t) { try { t.focus({ preventScroll: true }); } catch (_) {} }
      }, 60);
    }
    var released = false;
    return function release() {
      if (released) return;
      released = true;
      d.removeEventListener('keydown', onKey, true);
      held = Math.max(0, held - 1);
      if (!held) d.documentElement.classList.remove('vln-capture-open');
      if (prev && prev.focus && d.contains(prev)) { try { prev.focus({ preventScroll: true }); } catch (_) {} }
    };
  }

  w.VelunaCaptureGuard = {
    /* Called at load by an engine that is specific to this page type. */
    registerPageSpecific: function (id) {
      if (id && pageSpecific.indexOf(id) === -1) pageSpecific.push(id);
    },
    /* True when this page has a dedicated capture, so the generic one yields. */
    hasPageSpecific: function (exceptId) {
      var all = allPageSpecific();
      for (var i = 0; i < all.length; i++) {
        if (all[i] !== exceptId) return true;
      }
      return false;
    },
    pageSpecific: function () { return allPageSpecific(); },
    /* The only method the engines need. Returns false = do not interrupt. */
    mayShow: function (id) { return suppressReason(id) === null; },
    reason: function (id) { return suppressReason(id); },
    notifyOpen: function (id) { currentOwner = id || 'unknown'; },
    notifyClosed: function (id) { if (!id || currentOwner === id) currentOwner = null; },
    /* A visitor said no: no more prompts from THAT engine for the rest of the session. */
    notifyDismissed: function (id) { ssSet(dismissKey(id), String(Date.now())); },
    markAddToCart: markAddToCart,
    holdFocus: holdFocus,
    commerceVisible: function () { return commerceOnScreen; },
    /* Exposed for QA/diagnostics only. */
    state: function () {
      return {
        commerceOnScreen: commerceVisibleNow(),
        addedToCartThisSession: !!ssGet(ATC_SESSION_KEY),
        dismissedThisSession: (function () {
          var m = {};
          ['pdf-capture', 'pdp-mystery', 'quiz-exit', 'mc-lp-sheet', 'quiz-results-exit'].forEach(function (id) { var v = ssGet(dismissKey(id)); if (v) m[id] = v; });
          return m;
        })(),
        captureAgeMs: captureAgeMs(),
        purchaseIntentAgoMs: lastPurchaseIntent ? Date.now() - lastPurchaseIntent : null,
        currentOwner: currentOwner,
        overlayOpen: overlayOpen(),
        alreadyCaptured: alreadyCaptured(),
        pageSpecific: allPageSpecific()
      };
    }
  };

  function start() {
    watchCommerce();
    if (w.MutationObserver && d.body) {
      var debounce;
      new w.MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(watchCommerce, 250);
      }).observe(d.body, { childList: true, subtree: true });
    }
    w.addEventListener('scroll', function () {
      commerceOnScreen = watched.some(onScreen);
    }, { passive: true });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();
})(window, document);
